import React, { useState, useEffect } from 'react';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Users, DollarSign, Settings, ToggleLeft, ToggleRight, Check, X, UserCog, Trophy, Calendar, Gavel, LayoutDashboard, Home, LayoutGrid, List, User, Gift, Edit, Trash2, Plus, ExternalLink, TrendingUp, TrendingDown, Shield, Loader2, AlertTriangle, RotateCcw, UserX, Eye } from 'lucide-react';

function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const navigate = useNavigate();

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, { credentials: 'include' });
      const data = await response.json();
      
      if (!response.ok || !data.is_admin) {
        toast.error('Admin access required');
        navigate('/dashboard');
        return;
      }
      
      setUser(data);
    } catch (error) {
      navigate('/login');
    } finally {
      setIsLoading(false);
    }
  };

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'users', label: 'Registered Users', icon: Users },
    { id: 'artists', label: 'Artists', icon: Trophy },
    { id: 'admins', label: 'Admin Users', icon: UserCog },
    { id: 'judges', label: 'Judges', icon: Gavel },
    { id: 'deleted-accounts', label: 'Deleted Accounts', icon: UserX },
    { id: 'competitions', label: 'Competitions', icon: Calendar },
    { id: 'payments', label: 'Payments', icon: DollarSign },
    { id: 'earned-votes', label: 'Earned Votes', icon: Gift },
    { id: 'earn-methods', label: 'Earn Methods Config', icon: Edit },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <p className="text-muted-foreground">Loading Admin Dashboard...</p>
        <div className="w-48 h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-primary rounded-full animate-pulse" style={{width: '60%'}} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} glass-panel border-r border-white/10 transition-all duration-300 flex flex-col`}>
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center justify-between">
            {sidebarOpen && <h2 className="text-xl font-bold">Admin Panel</h2>}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-white/10 rounded-lg">
              {sidebarOpen ? <X className="h-5 w-5" /> : <LayoutDashboard className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === item.id
                    ? 'bg-primary text-white'
                    : 'hover:bg-white/10'
                }`}
                data-testid={`menu-${item.id}`}
              >
                <Icon className="h-5 w-5" />
                {sidebarOpen && <span>{item.label}</span>}
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/10">
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 transition-colors"
          >
            <Home className="h-5 w-5" />
            {sidebarOpen && <span>Back to Site</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2 tracking-tight text-accent">
              {menuItems.find(m => m.id === activeTab)?.label || 'Admin Dashboard'}
            </h1>
            <p className="text-muted-foreground">Manage your Tsarona platform</p>
          </div>

          {/* Content based on active tab */}
          {activeTab === 'overview' && <OverviewContent />}
          {activeTab === 'users' && <UsersContent filter="all" />}
          {activeTab === 'artists' && <UsersContent filter="artists" />}
          {activeTab === 'admins' && <UsersContent filter="admins" />}
          {activeTab === 'judges' && <UsersContent filter="judges" />}
          {activeTab === 'deleted-accounts' && <DeletedAccountsContent />}
          {activeTab === 'competitions' && <CompetitionsContent user={user} />}
          {activeTab === 'payments' && <PaymentsContent user={user} />}
          {activeTab === 'earned-votes' && <EarnedVotesContent user={user} />}
          {activeTab === 'earn-methods' && <EarnMethodsContent user={user} />}
          {activeTab === 'settings' && <SettingsContent />}
        </div>
      </div>
    </div>
  );
}

// Overview Component
function OverviewContent() {
  const [stats, setStats] = useState({ users: 0, artists: 0, judges: 0, posts: 0, competitions: 0 });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [usersRes, postsRes, competitionsRes] = await Promise.all([
        fetch(`${API}/admin/users-management`, { credentials: 'include' }),
        fetch(`${API}/posts`, { credentials: 'include' }),
        fetch(`${API}/competitions`, { credentials: 'include' })
      ]);
      
      const users = usersRes.ok ? await usersRes.json() : [];
      const posts = postsRes.ok ? await postsRes.json() : [];
      const competitions = competitionsRes.ok ? await competitionsRes.json() : [];
      
      setStats({
        users: Array.isArray(users) ? users.length : 0,
        artists: Array.isArray(users) ? users.filter(u => u.is_artist).length : 0,
        judges: Array.isArray(users) ? users.filter(u => u.is_judge).length : 0,
        posts: Array.isArray(posts) ? posts.length : 0,
        competitions: Array.isArray(competitions) ? competitions.length : 0
      });
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
      <Card className="glass-panel p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">Total Users</h3>
          <Users className="h-8 w-8 text-primary" />
        </div>
        <p className="text-4xl font-bold text-primary">{stats.users}</p>
      </Card>

      <Card className="glass-panel p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">Artists</h3>
          <Trophy className="h-8 w-8 text-secondary" />
        </div>
        <p className="text-4xl font-bold text-secondary">{stats.artists}</p>
      </Card>

      <Card className="glass-panel p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">Judges</h3>
          <Gavel className="h-8 w-8 text-accent" />
        </div>
        <p className="text-4xl font-bold text-accent">{stats.judges}</p>
      </Card>

      <Card className="glass-panel p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">Total Posts</h3>
          <LayoutDashboard className="h-8 w-8 text-yellow-500" />
        </div>
        <p className="text-4xl font-bold text-yellow-500">{stats.posts}</p>
      </Card>

      <Card className="glass-panel p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">Competitions</h3>
          <Calendar className="h-8 w-8 text-pink-500" />
        </div>
        <p className="text-4xl font-bold text-pink-500">{stats.competitions}</p>
      </Card>
    </div>
  );
}

// Users Content Component
function UsersContent({ filter }) {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, [filter]);

  const fetchUsers = async () => {
    try {
      const url = filter === 'all' 
        ? `${API}/admin/users-management`
        : `${API}/admin/users-management?filter_type=${filter}`;
      
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          toast.error('Session expired. Please log in again.');
          return;
        }
        throw new Error(`Server error: ${response.status}`);
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        // For "Registered Users" tab (filter=all), exclude artists, judges, and admins
        if (filter === 'all') {
          setUsers(data.filter(u => !u.is_artist && !u.is_judge && !u.is_admin));
        } else {
          setUsers(data);
        }
      } else {
        setUsers([]);
        toast.error('Unexpected response format');
      }
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle judge status (instead of just assign)
  const toggleJudgeStatus = async (userId) => {
    try {
      const response = await fetch(`${API}/admin/toggle-judge/${userId}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        fetchUsers();
      } else {
        toast.error(data.detail || 'Failed to toggle judge status');
      }
    } catch (error) {
      toast.error('Failed to toggle judge status');
    }
  };

  // Admin vote state
  const [showAdminVoteModal, setShowAdminVoteModal] = useState(null); // user_id or null
  const [adminVoteAction, setAdminVoteAction] = useState('increase');
  const [adminVoteCount, setAdminVoteCount] = useState(10);
  const [adminVoteReason, setAdminVoteReason] = useState('');
  const [isSubmittingAdminVote, setIsSubmittingAdminVote] = useState(false);

  const handleAdminVoteSubmit = async () => {
    if (!adminVoteReason.trim()) {
      toast.error('Please provide a reason for the vote adjustment');
      return;
    }
    if (adminVoteCount <= 0) {
      toast.error('Vote count must be greater than 0');
      return;
    }

    setIsSubmittingAdminVote(true);
    try {
      const response = await fetch(`${API}/admin/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          artist_id: showAdminVoteModal,
          vote_count: adminVoteCount,
          action: adminVoteAction,
          reason: adminVoteReason
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Votes ${adminVoteAction === 'increase' ? 'increased' : 'decreased'} successfully!`);
        setShowAdminVoteModal(null);
        setAdminVoteReason('');
        setAdminVoteCount(10);
        fetchUsers(); // Refresh the user list
      } else {
        toast.error(data.detail || 'Failed to submit admin vote');
      }
    } catch (error) {
      toast.error('Failed to submit admin vote');
    } finally {
      setIsSubmittingAdminVote(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Loading users...</p>
        <div className="w-40 h-1 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-primary rounded-full animate-pulse" style={{width: '70%'}} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Admin Vote Modal */}
      {showAdminVoteModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="glass-panel p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Shield className="h-5 w-5 text-purple-400" />
              Admin Vote Adjustment
            </h3>
            
            <div className="space-y-4">
              <div className="flex gap-2">
                <Button
                  onClick={() => setAdminVoteAction('increase')}
                  className={`flex-1 rounded-full ${adminVoteAction === 'increase' ? 'bg-green-600' : 'bg-gray-600'}`}
                >
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Increase
                </Button>
                <Button
                  onClick={() => setAdminVoteAction('decrease')}
                  className={`flex-1 rounded-full ${adminVoteAction === 'decrease' ? 'bg-red-600' : 'bg-gray-600'}`}
                >
                  <TrendingDown className="h-4 w-4 mr-2" />
                  Decrease
                </Button>
              </div>
              
              <div>
                <label className="block text-sm mb-1">Number of Votes</label>
                <Input
                  type="number"
                  min="1"
                  value={adminVoteCount}
                  onChange={(e) => setAdminVoteCount(parseInt(e.target.value) || 0)}
                  className="bg-white/10"
                />
              </div>
              
              <div>
                <label className="block text-sm mb-1">Reason (will be shown on user profile)</label>
                <Textarea
                  value={adminVoteReason}
                  onChange={(e) => setAdminVoteReason(e.target.value)}
                  placeholder="Enter reason for vote adjustment..."
                  className="bg-white/10 min-h-[80px]"
                />
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={handleAdminVoteSubmit}
                  disabled={isSubmittingAdminVote}
                  className={`flex-1 rounded-full ${adminVoteAction === 'increase' ? 'bg-green-600' : 'bg-red-600'}`}
                >
                  {isSubmittingAdminVote ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  Submit {adminVoteAction === 'increase' ? '+' : '-'}{adminVoteCount} Votes
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAdminVoteModal(null);
                    setAdminVoteReason('');
                  }}
                  className="rounded-full"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* View Toggle */}
      <div className="flex justify-end">
        <div className="flex gap-2 bg-white/5 p-1 rounded-full">
          <Button
            variant={viewMode === 'grid' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('grid')}
            className="rounded-full"
          >
            <LayoutGrid className="h-4 w-4 mr-2" />
            Cards
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('list')}
            className="rounded-full"
          >
            <List className="h-4 w-4 mr-2" />
            List
          </Button>
        </div>
      </div>

      {viewMode === 'list' ? (
        <Card className="glass-panel overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-white/5">
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Phone</th>
                <th className="text-left p-3">Email</th>
                <th className="text-center p-3">Artist</th>
                <th className="text-center p-3">Balance</th>
                <th className="text-center p-3">Total</th>
                <th className="text-center p-3">vJudge</th>
                <th className="text-center p-3">vAdmin</th>
                <th className="text-center p-3">vPay</th>
                <th className="text-center p-3">vEarn</th>
                <th className="text-center p-3">Judge</th>
                <th className="text-center p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.user_id} className="border-b border-white/10 hover:bg-white/5">
                  <td className="p-3">
                    <div 
                      className="flex items-center gap-2 cursor-pointer hover:text-primary transition-colors"
                      onClick={(e) => { e.stopPropagation(); window.open(`/profile/${user.user_id}`, '_blank'); }}
                    >
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {user.picture ? (
                          <img src={user.picture} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User className="h-4 w-4 text-primary" />
                        )}
                      </div>
                      <span className="font-medium truncate max-w-[120px] underline">{user.name}</span>
                    </div>
                  </td>
                  <td className="p-3 text-muted-foreground">{user.phone || '-'}</td>
                  <td className="p-3 text-muted-foreground truncate max-w-[150px]">{user.email || '-'}</td>
                  <td className="text-center p-3">
                    {user.is_artist ? <Check className="h-4 w-4 text-secondary mx-auto" /> : <X className="h-4 w-4 text-muted-foreground mx-auto" />}
                  </td>
                  <td className="text-center p-3">
                    <span className="font-bold text-blue-400">{user.free_votes_remaining || 0}</span>
                  </td>
                  <td className="text-center p-3 font-bold text-secondary">{user.votes_received || 0}</td>
                  <td className="text-center p-3">
                    <span className="text-accent">{user.votes_judge || 0}</span>
                  </td>
                  <td className="text-center p-3">
                    <span className="text-purple-400">{user.votes_admin || 0}</span>
                  </td>
                  <td className="text-center p-3">
                    <span className="text-green-400">{user.votes_paid || 0}</span>
                  </td>
                  <td className="text-center p-3">
                    <span className="text-yellow-400">{user.votes_earned || 0}</span>
                  </td>
                  <td className="text-center p-3">
                    {user.is_judge ? <Check className="h-4 w-4 text-accent mx-auto" /> : <X className="h-4 w-4 text-muted-foreground mx-auto" />}
                  </td>
                  <td className="text-center p-3">
                    <div className="flex gap-1 justify-center">
                      <Button
                        size="sm"
                        onClick={() => toggleJudgeStatus(user.user_id)}
                        className={`rounded-full text-xs ${user.is_judge ? 'bg-red-600 hover:bg-red-700' : 'bg-accent'}`}
                        data-testid={`toggle-judge-${user.user_id}`}
                      >
                        {user.is_judge ? 'Remove Judge' : 'Make Judge'}
                      </Button>
                      {user.is_artist && (
                        <Button
                          size="sm"
                          onClick={() => setShowAdminVoteModal(user.user_id)}
                          className="bg-purple-600 hover:bg-purple-700 rounded-full text-xs"
                          data-testid={`admin-vote-${user.user_id}`}
                        >
                          <Shield className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {users.map((user) => (
            <Card key={user.user_id} className="glass-panel p-4 hover:border-primary/50 transition-colors">
              <div 
                className="flex items-center gap-3 mb-3 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => window.open(`/profile/${user.user_id}`, '_blank')}
              >
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="h-5 w-5 text-primary" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-sm truncate underline">{user.name}</h3>
                  <p className="text-xs text-muted-foreground truncate">{user.phone || user.email}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Balance</p>
                  <p className="font-bold text-blue-400">{user.free_votes_remaining || 0}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-5 gap-1 text-center text-xs mb-3">
                <div className="bg-white/5 rounded p-1.5">
                  <p className="text-muted-foreground text-[10px]">Total</p>
                  <p className="font-bold text-secondary">{user.votes_received || 0}</p>
                </div>
                <div className="bg-white/5 rounded p-1.5">
                  <p className="text-muted-foreground text-[10px]">Judge</p>
                  <p className="font-bold text-accent">{user.votes_judge || 0}</p>
                </div>
                <div className="bg-white/5 rounded p-1.5">
                  <p className="text-muted-foreground text-[10px]">Admin</p>
                  <p className="font-bold text-purple-400">{user.votes_admin || 0}</p>
                </div>
                <div className="bg-white/5 rounded p-1.5">
                  <p className="text-muted-foreground text-[10px]">Paid</p>
                  <p className="font-bold text-green-400">{user.votes_paid || 0}</p>
                </div>
                <div className="bg-white/5 rounded p-1.5">
                  <p className="text-muted-foreground text-[10px]">Earn</p>
                  <p className="font-bold text-yellow-400">{user.votes_earned || 0}</p>
                </div>
              </div>

              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded text-xs ${user.is_artist ? 'bg-secondary/20 text-secondary' : 'bg-white/10 text-muted-foreground'}`}>
                    {user.is_artist ? 'Artist' : 'User'}
                  </span>
                  {user.is_judge && (
                    <span className="px-2 py-0.5 rounded text-xs bg-accent/20 text-accent">Judge</span>
                  )}
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => toggleJudgeStatus(user.user_id)}
                  className={`flex-1 rounded-full text-xs ${user.is_judge ? 'bg-red-600 hover:bg-red-700' : 'bg-accent'}`}
                >
                  {user.is_judge ? 'Remove Judge' : 'Make Judge'}
                </Button>
                {user.is_artist && (
                  <Button
                    size="sm"
                    onClick={() => setShowAdminVoteModal(user.user_id)}
                    className="bg-purple-600 hover:bg-purple-700 rounded-full text-xs"
                  >
                    <Shield className="h-3 w-3 mr-1" />
                    Admin Vote
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// Competitions Content
function CompetitionsContent({ user }) {
  const [competitions, setCompetitions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCompetition, setEditingCompetition] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchCompetitions();
  }, []);

  const fetchCompetitions = async () => {
    try {
      const response = await fetch(`${API}/competitions`, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      setCompetitions(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load competitions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCompetition = async (e) => {
    e.preventDefault();
    try {
      const params = new URLSearchParams({
        title: formData.title,
        description: formData.description,
        start_date: formData.start_date,
        end_date: formData.end_date
      });
      
      const response = await fetch(`${API}/admin/competitions?${params}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Competition created successfully!');
        setShowCreateForm(false);
        setFormData({ title: '', description: '', start_date: '', end_date: '' });
        fetchCompetitions();
      } else {
        toast.error('Failed to create competition');
      }
    } catch (error) {
      toast.error('Failed to create competition');
    }
  };

  const openEditModal = (comp) => {
    setEditingCompetition(comp);
    setFormData({
      title: comp.title,
      description: comp.description,
      start_date: comp.start_date ? comp.start_date.slice(0, 16) : '',
      end_date: comp.end_date ? comp.end_date.slice(0, 16) : ''
    });
    setShowEditModal(true);
  };

  const handleEditCompetition = async (e) => {
    e.preventDefault();
    try {
      const params = new URLSearchParams({
        title: formData.title,
        description: formData.description,
        start_date: formData.start_date,
        end_date: formData.end_date
      });
      
      const response = await fetch(`${API}/admin/competitions/${editingCompetition.event_id}?${params}`, {
        method: 'PATCH',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Competition updated successfully!');
        setShowEditModal(false);
        setEditingCompetition(null);
        setFormData({ title: '', description: '', start_date: '', end_date: '' });
        fetchCompetitions();
      } else {
        toast.error('Failed to update competition');
      }
    } catch (error) {
      toast.error('Failed to update competition');
    }
  };

  const toggleRegistration = async (eventId, currentStatus) => {
    const newStatus = currentStatus === 'open' ? 'closed' : 'open';
    try {
      const response = await fetch(`${API}/admin/competitions/${eventId}/registration?status=${newStatus}`, {
        method: 'PATCH',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success(`Registration ${newStatus}`);
        fetchCompetitions();
      }
    } catch (error) {
      toast.error('Failed to update registration status');
    }
  };

  const toggleVoting = async (eventId, currentStatus) => {
    const newStatus = currentStatus === 'open' ? 'closed' : 'open';
    try {
      const response = await fetch(`${API}/admin/competitions/${eventId}/voting?status=${newStatus}`, {
        method: 'PATCH',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success(`Voting ${newStatus}`);
        fetchCompetitions();
      }
    } catch (error) {
      toast.error('Failed to update voting status');
    }
  };

  const toggleActive = async (eventId, currentStatus) => {
    const newStatus = !currentStatus;
    try {
      const response = await fetch(`${API}/admin/competitions/${eventId}/active?is_active=${newStatus}`, {
        method: 'PATCH',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success(`Competition ${newStatus ? 'enabled' : 'disabled'}`);
        fetchCompetitions();
      }
    } catch (error) {
      toast.error('Failed to update competition status');
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Loading competitions...</p>
        <div className="w-40 h-1 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-primary rounded-full animate-pulse" style={{width: '70%'}} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Edit Competition Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="glass-panel p-6 max-w-lg w-full">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-bold">Edit Competition</h4>
              <Button variant="ghost" size="sm" onClick={() => setShowEditModal(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <form onSubmit={handleEditCompetition} className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 rounded-lg p-3 focus:border-primary outline-none"
                  placeholder="Competition Title"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 rounded-lg p-3 focus:border-primary outline-none min-h-[100px]"
                  placeholder="Competition description..."
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Start Date</label>
                  <input
                    type="datetime-local"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 focus:border-primary outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">End Date</label>
                  <input
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 focus:border-primary outline-none"
                    required
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <Button type="submit" className="flex-1 bg-primary rounded-full">
                  Save Changes
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowEditModal(false)} className="rounded-full">
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h3 className="text-xl font-bold">Manage Competitions</h3>
        <Button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="bg-secondary text-black rounded-full"
          data-testid="create-competition-btn"
        >
          {showCreateForm ? 'Cancel' : '+ Create Competition'}
        </Button>
      </div>

      {showCreateForm && (
        <Card className="glass-panel p-6">
          <h4 className="text-lg font-bold mb-4">Create New Competition</h4>
          <form onSubmit={handleCreateCompetition} className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="w-full bg-white/5 border border-white/10 rounded-lg p-3 focus:border-primary outline-none"
                placeholder="Competition Title"
                required
                data-testid="competition-title-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full bg-white/5 border border-white/10 rounded-lg p-3 focus:border-primary outline-none min-h-[100px]"
                placeholder="Competition description..."
                required
                data-testid="competition-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Start Date</label>
                <input
                  type="datetime-local"
                  value={formData.start_date}
                  onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 rounded-lg p-3 focus:border-primary outline-none"
                  required
                  data-testid="competition-start-date-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">End Date</label>
                <input
                  type="datetime-local"
                  value={formData.end_date}
                  onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 rounded-lg p-3 focus:border-primary outline-none"
                  required
                  data-testid="competition-end-date-input"
                />
              </div>
            </div>
            <Button type="submit" className="w-full bg-primary rounded-full" data-testid="submit-competition-btn">
              Create Competition
            </Button>
          </form>
        </Card>
      )}

      <div className="space-y-4">
        {competitions.length === 0 ? (
          <Card className="glass-panel p-8 text-center">
            <p className="text-muted-foreground">No competitions created yet</p>
          </Card>
        ) : (
          competitions.map((comp) => (
            <Card key={comp.event_id} className="glass-panel p-6" data-testid={`competition-card-${comp.event_id}`}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h4 className="text-xl font-bold">{comp.title}</h4>
                  <p className="text-muted-foreground text-sm">{comp.description}</p>
                  <div className="flex gap-4 mt-2 text-sm">
                    <span>Start: {new Date(comp.start_date).toLocaleDateString()}</span>
                    <span>End: {new Date(comp.end_date).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  {/* Enable/Disable Checkbox */}
                  <label className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-full cursor-pointer hover:bg-white/10 transition-colors">
                    <input
                      type="checkbox"
                      checked={comp.is_active !== false}
                      onChange={() => toggleActive(comp.event_id, comp.is_active !== false)}
                      className="w-4 h-4 accent-secondary rounded"
                      data-testid={`toggle-active-${comp.event_id}`}
                    />
                    <span className={`text-sm font-medium ${comp.is_active !== false ? 'text-secondary' : 'text-muted-foreground'}`}>
                      {comp.is_active !== false ? 'Active' : 'Disabled'}
                    </span>
                  </label>
                  <Button
                    size="sm"
                    onClick={() => toggleRegistration(comp.event_id, comp.registration_status)}
                    className={`rounded-full ${comp.registration_status === 'open' ? 'bg-secondary text-black' : 'bg-red-500'}`}
                    data-testid={`toggle-registration-${comp.event_id}`}
                  >
                    {comp.registration_status === 'open' ? <ToggleRight className="mr-1 h-4 w-4" /> : <ToggleLeft className="mr-1 h-4 w-4" />}
                    Registration: {comp.registration_status}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => toggleVoting(comp.event_id, comp.voting_status)}
                    className={`rounded-full ${comp.voting_status === 'open' ? 'bg-primary' : 'bg-red-500'}`}
                    data-testid={`toggle-voting-${comp.event_id}`}
                  >
                    {comp.voting_status === 'open' ? <ToggleRight className="mr-1 h-4 w-4" /> : <ToggleLeft className="mr-1 h-4 w-4" />}
                    Voting: {comp.voting_status}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEditModal(comp)}
                    className="rounded-full"
                    data-testid={`edit-competition-${comp.event_id}`}
                  >
                    Edit
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

// Proof Viewer - shows proof image or link
function ProofViewer({ payment }) {
  const [showImage, setShowImage] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);

  const loadProofImage = async () => {
    if (payment.proof_storage_path) {
      try {
        const response = await fetch(`${API}/files/payments/${payment.proof_storage_path}`, { credentials: 'include' });
        if (response.ok) {
          const blob = await response.blob();
          setImageUrl(URL.createObjectURL(blob));
        }
      } catch { /* ignore */ }
    }
    setShowImage(true);
  };

  if (!payment.proof_storage_path && !payment.proof_url) {
    return <span className="text-xs text-muted-foreground">No proof</span>;
  }

  return (
    <>
      <button
        onClick={loadProofImage}
        className="text-primary hover:underline text-sm flex items-center gap-1"
        data-testid={`view-proof-${payment.payment_id}`}
      >
        <Eye className="h-3 w-3" /> View Proof
      </button>
      {showImage && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => { setShowImage(false); if (imageUrl) URL.revokeObjectURL(imageUrl); }}>
          <div className="max-w-2xl max-h-[80vh] bg-zinc-900 rounded-xl p-4 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => { setShowImage(false); if (imageUrl) URL.revokeObjectURL(imageUrl); }} className="absolute top-2 right-2 text-white hover:text-red-400">
              <X className="h-6 w-6" />
            </button>
            <p className="text-sm text-muted-foreground mb-2">Proof from {payment.user_name || payment.user_id} - R{payment.amount}</p>
            {imageUrl ? (
              payment.proof_content_type === 'application/pdf' ? (
                <p className="text-center py-8 text-muted-foreground">PDF file uploaded: {payment.proof_filename}</p>
              ) : (
                <img src={imageUrl} alt="Proof of Payment" className="max-h-[65vh] w-auto mx-auto rounded-lg" />
              )
            ) : payment.proof_url ? (
              <a href={payment.proof_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Open external proof link</a>
            ) : (
              <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// Payments Content
function PaymentsContent({ user }) {
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchPayments();
    fetchUsers();
  }, [filter]);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/admin/users-management`, { credentials: 'include' });
      if (!response.ok) return;
      const data = await response.json();
      if (!Array.isArray(data)) return;
      const userMap = {};
      data.forEach(u => {
        userMap[u.user_id] = u;
      });
      setUsers(userMap);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const fetchPayments = async () => {
    try {
      const url = filter === 'all' 
        ? `${API}/admin/payments`
        : `${API}/admin/payments?status=${filter}`;
      
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      setPayments(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApproval = async (paymentId, approved) => {
    try {
      const response = await fetch(`${API}/admin/payments/${paymentId}/approve?approved=${approved}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(`Payment ${approved ? 'approved' : 'rejected'}`);
        fetchPayments();
      }
    } catch (error) {
      toast.error('Failed to process payment');
    }
  };

  const getUserName = (userId) => {
    const userInfo = users[userId];
    return userInfo ? (userInfo.name || userInfo.stage_name || 'Unknown') : userId;
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Loading payments...</p>
        <div className="w-40 h-1 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-primary rounded-full animate-pulse" style={{width: '70%'}} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {['all', 'pending', 'approved', 'rejected'].map((status) => (
          <Button
            key={status}
            size="sm"
            variant={filter === status ? 'default' : 'outline'}
            onClick={() => setFilter(status)}
            className="rounded-full capitalize"
            data-testid={`filter-${status}`}
          >
            {status}
          </Button>
        ))}
      </div>

      <Card className="glass-panel overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left p-4">Payment ID</th>
              <th className="text-left p-4">User</th>
              <th className="text-center p-4">Amount (R)</th>
              <th className="text-center p-4">Votes</th>
              <th className="text-center p-4">Status</th>
              <th className="text-left p-4">Proof</th>
              <th className="text-center p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {payments.length === 0 ? (
              <tr>
                <td colSpan="7" className="p-8 text-center text-muted-foreground">
                  No payments found
                </td>
              </tr>
            ) : (
              payments.map((payment) => (
                <tr key={payment.payment_id} className="border-b border-white/10 hover:bg-white/5">
                  <td className="p-4 font-mono text-sm">{payment.payment_id}</td>
                  <td className="p-4">
                    <a 
                      href={`/profile/${payment.user_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline font-medium"
                    >
                      {payment.user_name || getUserName(payment.user_id)}
                    </a>
                  </td>
                  <td className="text-center p-4 font-bold text-secondary">R{payment.amount}</td>
                  <td className="text-center p-4 font-bold text-primary">{payment.votes_to_add}</td>
                  <td className="text-center p-4">
                    <span className={`px-3 py-1 rounded-full text-sm ${
                      payment.status === 'approved' ? 'bg-secondary/20 text-secondary' :
                      payment.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {payment.status}
                    </span>
                  </td>
                  <td className="p-4">
                    <ProofViewer payment={payment} />
                  </td>
                  <td className="text-center p-4">
                    {payment.status === 'pending' && user?.is_super_admin && (
                      <div className="flex gap-2 justify-center">
                        <Button
                          size="sm"
                          onClick={() => handleApproval(payment.payment_id, true)}
                          className="bg-secondary text-black rounded-full"
                          data-testid={`approve-${payment.payment_id}`}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleApproval(payment.payment_id, false)}
                          className="bg-red-500 rounded-full"
                          data-testid={`reject-${payment.payment_id}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// Earned Votes Content
function EarnedVotesContent({ user }) {
  const [submissions, setSubmissions] = useState([]);
  const [users, setUsers] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchSubmissions();
    fetchUsers();
  }, [filter]);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/admin/users-management`, { credentials: 'include' });
      if (!response.ok) return;
      const data = await response.json();
      if (!Array.isArray(data)) return;
      const userMap = {};
      data.forEach(u => {
        userMap[u.user_id] = u;
      });
      setUsers(userMap);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const fetchSubmissions = async () => {
    try {
      const url = filter === 'all' 
        ? `${API}/admin/earned-votes`
        : `${API}/admin/earned-votes?status=${filter}`;
      
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      setSubmissions(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load earned vote submissions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApproval = async (submissionId, approved) => {
    try {
      const response = await fetch(`${API}/admin/earned-votes/${submissionId}/approve?approved=${approved}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(`Submission ${approved ? 'approved' : 'rejected'}`);
        fetchSubmissions();
      } else {
        toast.error('Failed to process submission');
      }
    } catch (error) {
      toast.error('Failed to process submission');
    }
  };

  const getUserName = (userId) => {
    const userInfo = users[userId];
    return userInfo ? (userInfo.name || userInfo.stage_name || 'Unknown') : userId;
  };

  const getMethodLabel = (methodId) => {
    const methods = {
      'share_social': 'Share on Social Media',
      'refer_friend': 'Refer a Friend',
      'leave_comment': 'Leave a Comment',
      'rate_artist': 'Rate an Artist',
      'watch_video': 'Watch Artist Video',
      'daily_login': 'Daily Login Bonus'
    };
    return methods[methodId] || methodId;
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Loading submissions...</p>
        <div className="w-40 h-1 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-primary rounded-full animate-pulse" style={{width: '70%'}} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {['all', 'pending', 'approved', 'rejected'].map((status) => (
          <Button
            key={status}
            size="sm"
            variant={filter === status ? 'default' : 'outline'}
            onClick={() => setFilter(status)}
            className="rounded-full capitalize"
            data-testid={`earned-filter-${status}`}
          >
            {status}
          </Button>
        ))}
      </div>

      <Card className="glass-panel overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left p-4">User</th>
              <th className="text-left p-4">Task</th>
              <th className="text-center p-4">Votes</th>
              <th className="text-left p-4">Proof</th>
              <th className="text-center p-4">Status</th>
              <th className="text-left p-4">Submitted</th>
              <th className="text-center p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {submissions.length === 0 ? (
              <tr>
                <td colSpan="7" className="p-8 text-center text-muted-foreground">
                  No earned vote submissions found
                </td>
              </tr>
            ) : (
              submissions.map((submission) => (
                <tr key={submission.submission_id} className="border-b border-white/10 hover:bg-white/5">
                  <td className="p-4">
                    <a 
                      href={`/profile/${submission.user_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline font-medium"
                    >
                      {getUserName(submission.user_id)}
                    </a>
                  </td>
                  <td className="p-4 text-sm">{getMethodLabel(submission.method_id)}</td>
                  <td className="text-center p-4 font-bold text-secondary">{submission.votes_earned}</td>
                  <td className="p-4">
                    {submission.proof_url ? (
                      submission.proof_url.startsWith('data:image') ? (
                        <div className="relative group">
                          <img 
                            src={submission.proof_url} 
                            alt="Proof" 
                            className="w-16 h-16 object-cover rounded cursor-pointer"
                            onClick={() => window.open(submission.proof_url, '_blank')}
                          />
                          <span className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center text-xs rounded transition-opacity">
                            View
                          </span>
                        </div>
                      ) : (
                        <a 
                          href={submission.proof_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline text-sm flex items-center gap-1"
                        >
                          <ExternalLink className="h-3 w-3" />
                          View Link
                        </a>
                      )
                    ) : (
                      <span className="text-muted-foreground text-sm">No proof</span>
                    )}
                  </td>
                  <td className="text-center p-4">
                    <span className={`px-3 py-1 rounded-full text-sm ${
                      submission.status === 'approved' ? 'bg-secondary/20 text-secondary' :
                      submission.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {submission.status}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    {new Date(submission.created_at).toLocaleString()}
                  </td>
                  <td className="text-center p-4">
                    {submission.status === 'pending' && user?.is_super_admin && (
                      <div className="flex gap-2 justify-center">
                        <Button
                          size="sm"
                          onClick={() => handleApproval(submission.submission_id, true)}
                          className="bg-secondary text-black rounded-full"
                          data-testid={`approve-earned-${submission.submission_id}`}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleApproval(submission.submission_id, false)}
                          className="bg-red-500 rounded-full"
                          data-testid={`reject-earned-${submission.submission_id}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// Earn Methods Config Content
function EarnMethodsContent({ user }) {
  const [methods, setMethods] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [formData, setFormData] = useState({
    action: '',
    votes_earned: 1,
    rules: '',
    action_button_text: '',
    action_button_link: '',
    is_enabled: true,
    requires_approval: false
  });

  useEffect(() => {
    fetchMethods();
  }, []);

  const fetchMethods = async () => {
    try {
      const response = await fetch(`${API}/admin/earn-methods`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setMethods(data);
      }
    } catch (error) {
      toast.error('Failed to load earn methods');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      action: '',
      votes_earned: 1,
      rules: '',
      action_button_text: '',
      action_button_link: '',
      is_enabled: true,
      requires_approval: false
    });
  };

  const handleCreateMethod = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/admin/earn-methods`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Earn method created!');
        setShowCreateForm(false);
        resetForm();
        fetchMethods();
      } else {
        toast.error('Failed to create method');
      }
    } catch (error) {
      toast.error('Failed to create method');
    }
  };

  const handleUpdateMethod = async (e) => {
    e.preventDefault();
    if (!editingMethod) return;

    try {
      const response = await fetch(`${API}/admin/earn-methods/${editingMethod.method_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Earn method updated!');
        setEditingMethod(null);
        resetForm();
        fetchMethods();
      } else {
        toast.error('Failed to update method');
      }
    } catch (error) {
      toast.error('Failed to update method');
    }
  };

  const handleDeleteMethod = async (methodId) => {
    if (!confirm('Are you sure you want to delete this earn method?')) return;

    try {
      const response = await fetch(`${API}/admin/earn-methods/${methodId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Earn method deleted');
        fetchMethods();
      } else {
        toast.error('Failed to delete method');
      }
    } catch (error) {
      toast.error('Failed to delete method');
    }
  };

  const handleToggleEnabled = async (method) => {
    try {
      const response = await fetch(`${API}/admin/earn-methods/${method.method_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_enabled: !method.is_enabled })
      });

      if (response.ok) {
        toast.success(`Method ${!method.is_enabled ? 'enabled' : 'disabled'}`);
        fetchMethods();
      }
    } catch (error) {
      toast.error('Failed to update method');
    }
  };

  const openEditModal = (method) => {
    setEditingMethod(method);
    setFormData({
      action: method.action,
      votes_earned: method.votes_earned,
      rules: method.rules || '',
      action_button_text: method.action_button_text || '',
      action_button_link: method.action_button_link || '',
      is_enabled: method.is_enabled,
      requires_approval: method.requires_approval || false
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Loading earn methods...</p>
        <div className="w-40 h-1 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-primary rounded-full animate-pulse" style={{width: '70%'}} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Edit Modal */}
      {editingMethod && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="glass-panel p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-bold">Edit Earn Method</h4>
              <Button variant="ghost" size="sm" onClick={() => { setEditingMethod(null); resetForm(); }}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <form onSubmit={handleUpdateMethod} className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Action Name</label>
                <input
                  type="text"
                  value={formData.action}
                  onChange={(e) => setFormData({...formData, action: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 rounded-lg p-3 focus:border-primary outline-none"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Votes Earned</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.votes_earned}
                    onChange={(e) => setFormData({...formData, votes_earned: parseInt(e.target.value) || 1})}
                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 focus:border-primary outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Button Text</label>
                  <input
                    type="text"
                    value={formData.action_button_text}
                    onChange={(e) => setFormData({...formData, action_button_text: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 focus:border-primary outline-none"
                    placeholder="Go to Profile"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Button Link</label>
                <input
                  type="text"
                  value={formData.action_button_link}
                  onChange={(e) => setFormData({...formData, action_button_link: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 rounded-lg p-3 focus:border-primary outline-none"
                  placeholder="/edit-profile"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Rules & Conditions</label>
                <textarea
                  value={formData.rules}
                  onChange={(e) => setFormData({...formData, rules: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 rounded-lg p-3 focus:border-primary outline-none min-h-[80px]"
                  placeholder="Link must be valid..."
                />
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_enabled}
                    onChange={(e) => setFormData({...formData, is_enabled: e.target.checked})}
                    className="w-4 h-4 accent-secondary rounded"
                  />
                  <span className="text-sm">Enabled</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.requires_approval}
                    onChange={(e) => setFormData({...formData, requires_approval: e.target.checked})}
                    className="w-4 h-4 accent-accent rounded"
                  />
                  <span className="text-sm">Requires Approval</span>
                </label>
              </div>
              <div className="flex gap-3">
                <Button type="submit" className="flex-1 bg-primary rounded-full">
                  Save Changes
                </Button>
                <Button type="button" variant="outline" onClick={() => { setEditingMethod(null); resetForm(); }} className="rounded-full">
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-bold">Manage Earn Methods</h3>
          <p className="text-sm text-muted-foreground">Configure how users can earn free votes</p>
        </div>
        <Button
          onClick={() => { setShowCreateForm(!showCreateForm); resetForm(); }}
          className="bg-secondary text-black rounded-full"
          data-testid="create-earn-method-btn"
        >
          {showCreateForm ? <X className="mr-1 h-4 w-4" /> : <Plus className="mr-1 h-4 w-4" />}
          {showCreateForm ? 'Cancel' : 'Add Method'}
        </Button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <Card className="glass-panel p-6">
          <h4 className="text-lg font-bold mb-4">Add New Earn Method</h4>
          <form onSubmit={handleCreateMethod} className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Action Name *</label>
              <input
                type="text"
                value={formData.action}
                onChange={(e) => setFormData({...formData, action: e.target.value})}
                className="w-full bg-white/5 border border-white/10 rounded-lg p-3 focus:border-primary outline-none"
                placeholder="Add YouTube Link to Profile"
                required
                data-testid="earn-method-action-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Votes Earned *</label>
                <input
                  type="number"
                  min="1"
                  value={formData.votes_earned}
                  onChange={(e) => setFormData({...formData, votes_earned: parseInt(e.target.value) || 1})}
                  className="w-full bg-white/5 border border-white/10 rounded-lg p-3 focus:border-primary outline-none"
                  required
                  data-testid="earn-method-votes-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Button Text</label>
                <input
                  type="text"
                  value={formData.action_button_text}
                  onChange={(e) => setFormData({...formData, action_button_text: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 rounded-lg p-3 focus:border-primary outline-none"
                  placeholder="Go to Profile"
                  data-testid="earn-method-btn-text-input"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Button Link</label>
              <input
                type="text"
                value={formData.action_button_link}
                onChange={(e) => setFormData({...formData, action_button_link: e.target.value})}
                className="w-full bg-white/5 border border-white/10 rounded-lg p-3 focus:border-primary outline-none"
                placeholder="/edit-profile"
                data-testid="earn-method-btn-link-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Rules & Conditions</label>
              <textarea
                value={formData.rules}
                onChange={(e) => setFormData({...formData, rules: e.target.value})}
                className="w-full bg-white/5 border border-white/10 rounded-lg p-3 focus:border-primary outline-none min-h-[80px]"
                placeholder="Link must be valid..."
                data-testid="earn-method-rules-input"
              />
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_enabled}
                  onChange={(e) => setFormData({...formData, is_enabled: e.target.checked})}
                  className="w-4 h-4 accent-secondary rounded"
                />
                <span className="text-sm">Enabled</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.requires_approval}
                  onChange={(e) => setFormData({...formData, requires_approval: e.target.checked})}
                  className="w-4 h-4 accent-accent rounded"
                />
                <span className="text-sm">Requires Admin Approval</span>
              </label>
            </div>
            <Button type="submit" className="w-full bg-primary rounded-full" data-testid="submit-earn-method-btn">
              Create Earn Method
            </Button>
          </form>
        </Card>
      )}

      {/* Methods Table */}
      <Card className="glass-panel overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left p-4">Action</th>
              <th className="text-center p-4">Votes</th>
              <th className="text-left p-4">Rules</th>
              <th className="text-left p-4">Button</th>
              <th className="text-center p-4">Approval</th>
              <th className="text-center p-4">Status</th>
              <th className="text-center p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {methods.length === 0 ? (
              <tr>
                <td colSpan="7" className="p-8 text-center text-muted-foreground">
                  No earn methods configured. Click "Add Method" to create one.
                </td>
              </tr>
            ) : (
              methods.map((method) => (
                <tr key={method.method_id} className="border-b border-white/10 hover:bg-white/5">
                  <td className="p-4">
                    <span className="font-medium">{method.action}</span>
                  </td>
                  <td className="text-center p-4">
                    <span className="text-secondary font-bold">+{method.votes_earned}</span>
                  </td>
                  <td className="p-4 max-w-[200px]">
                    <span className="text-sm text-muted-foreground truncate block">{method.rules || '-'}</span>
                  </td>
                  <td className="p-4">
                    <div className="text-xs">
                      <span className="text-muted-foreground">{method.action_button_text}</span>
                      <br />
                      <span className="text-primary">{method.action_button_link}</span>
                    </div>
                  </td>
                  <td className="text-center p-4">
                    {method.requires_approval ? (
                      <span className="px-2 py-1 bg-accent/20 text-accent rounded-full text-xs">Required</span>
                    ) : (
                      <span className="px-2 py-1 bg-white/10 text-muted-foreground rounded-full text-xs">Auto</span>
                    )}
                  </td>
                  <td className="text-center p-4">
                    <label className="flex items-center justify-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={method.is_enabled}
                        onChange={() => handleToggleEnabled(method)}
                        className="w-4 h-4 accent-secondary rounded"
                      />
                      <span className={`text-xs ${method.is_enabled ? 'text-secondary' : 'text-muted-foreground'}`}>
                        {method.is_enabled ? 'Active' : 'Disabled'}
                      </span>
                    </label>
                  </td>
                  <td className="text-center p-4">
                    <div className="flex gap-2 justify-center">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditModal(method)}
                        className="rounded-full"
                        data-testid={`edit-method-${method.method_id}`}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteMethod(method.method_id)}
                        className="rounded-full text-red-400 hover:text-red-300 hover:border-red-400"
                        data-testid={`delete-method-${method.method_id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      {/* Info Card */}
      <Card className="glass-panel p-4 border-primary/30">
        <div className="flex items-start gap-3">
          <Gift className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="mb-2"><strong className="text-white">Auto vs Approval:</strong></p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Auto:</strong> Votes are granted automatically when the user completes the action</li>
              <li><strong>Requires Approval:</strong> User submits proof, admin reviews and approves/rejects</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
}

// Settings Content
function SettingsContent() {
  const [config, setConfig] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [judgeMultiplier, setJudgeMultiplier] = useState(10);
  const [backgroundUrl, setBackgroundUrl] = useState('');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API}/admin/system-config`, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      setConfig(data || {});
      setJudgeMultiplier(data?.judge_vote_multiplier || 10);
      setBackgroundUrl(data?.landing_background || '');
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  const updateConfig = async (key, value) => {
    try {
      const response = await fetch(`${API}/admin/system-config?config_key=${key}&config_value=${value}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Setting updated');
        fetchConfig();
      }
    } catch (error) {
      toast.error('Failed to update setting');
    }
  };

  const toggleDemoMode = () => {
    updateConfig('demo_mode', !config.demo_mode);
  };

  const saveJudgeMultiplier = () => {
    updateConfig('judge_vote_multiplier', judgeMultiplier);
  };

  const saveBackgroundUrl = () => {
    updateConfig('landing_background', backgroundUrl);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Loading settings...</p>
        <div className="w-40 h-1 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-primary rounded-full animate-pulse" style={{width: '70%'}} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="glass-panel p-6">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-xl font-bold">Demo Mode</h4>
            <p className="text-muted-foreground text-sm">
              When disabled, demo content (demo artists and posts) will be hidden
            </p>
          </div>
          <Button
            onClick={toggleDemoMode}
            className={`rounded-full ${config.demo_mode ? 'bg-secondary text-black' : 'bg-red-500'}`}
            data-testid="toggle-demo-mode"
          >
            {config.demo_mode ? <ToggleRight className="mr-2 h-5 w-5" /> : <ToggleLeft className="mr-2 h-5 w-5" />}
            {config.demo_mode ? 'Enabled' : 'Disabled'}
          </Button>
        </div>
      </Card>

      <Card className="glass-panel p-6">
        <h4 className="text-xl font-bold mb-4">Judge Vote Multiplier</h4>
        <p className="text-muted-foreground text-sm mb-4">
          Judge ratings are multiplied by this value when calculating total votes
        </p>
        <div className="flex gap-4">
          <input
            type="number"
            value={judgeMultiplier}
            onChange={(e) => setJudgeMultiplier(parseInt(e.target.value) || 1)}
            min="1"
            max="100"
            className="flex-1 bg-white/5 border border-white/10 rounded-lg p-3 focus:border-primary outline-none"
            data-testid="judge-multiplier-input"
          />
          <Button
            onClick={saveJudgeMultiplier}
            className="bg-primary rounded-full"
            data-testid="save-judge-multiplier"
          >
            Save
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Example: A judge rating of 8 with multiplier {judgeMultiplier} = {8 * judgeMultiplier} vote points
        </p>
      </Card>

      <Card className="glass-panel p-6">
        <h4 className="text-xl font-bold mb-4">Landing Page Background</h4>
        <p className="text-muted-foreground text-sm mb-4">
          Set a custom background image URL for the landing page
        </p>
        <div className="flex gap-4">
          <input
            type="url"
            value={backgroundUrl}
            onChange={(e) => setBackgroundUrl(e.target.value)}
            className="flex-1 bg-white/5 border border-white/10 rounded-lg p-3 focus:border-primary outline-none"
            placeholder="https://example.com/background.jpg"
            data-testid="background-url-input"
          />
          <Button
            onClick={saveBackgroundUrl}
            className="bg-primary rounded-full"
            data-testid="save-background-url"
          >
            Save
          </Button>
        </div>
        {backgroundUrl && (
          <div className="mt-4">
            <p className="text-sm font-medium mb-2">Preview:</p>
            <img 
              src={backgroundUrl} 
              alt="Background preview" 
              className="w-full max-h-48 object-cover rounded-lg"
              onError={(e) => e.target.src = 'https://via.placeholder.com/800x200?text=Invalid+URL'}
            />
          </div>
        )}
      </Card>
    </div>
  );
}

// Deleted Accounts Management Component
function DeletedAccountsContent() {
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState('pending'); // pending, deleted
  const [actionLoading, setActionLoading] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [activeSubTab]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      if (activeSubTab === 'pending') {
        const response = await fetch(`${API}/admin/deletion-requests?status=pending`, { credentials: 'include' });
        const data = await response.json();
        if (response.ok) setDeletionRequests(data);
      } else {
        const response = await fetch(`${API}/admin/deleted-users`, { credentials: 'include' });
        const data = await response.json();
        if (response.ok) setDeletedUsers(data);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApproveDelete = async (userId) => {
    setActionLoading(userId);
    try {
      const response = await fetch(`${API}/admin/users/${userId}/approve-deletion`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Account deleted (hidden)');
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to delete account');
      }
    } catch (error) {
      toast.error('Failed to delete account');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRecoverAccount = async (userId) => {
    setActionLoading(userId);
    try {
      const response = await fetch(`${API}/admin/users/${userId}/recover-account`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Account recovered');
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to recover account');
      }
    } catch (error) {
      toast.error('Failed to recover account');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePermanentDelete = async (userId) => {
    if (!window.confirm('Are you sure? This action is PERMANENT and cannot be undone!')) {
      return;
    }
    setActionLoading(userId);
    try {
      const response = await fetch(`${API}/admin/users/${userId}/permanent-delete`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Account permanently deleted');
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to permanently delete');
      }
    } catch (error) {
      toast.error('Failed to permanently delete');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="flex gap-4">
        <Button
          onClick={() => setActiveSubTab('pending')}
          className={`rounded-full ${activeSubTab === 'pending' ? 'bg-yellow-500 text-black' : 'bg-white/10'}`}
        >
          <AlertTriangle className="mr-2 h-4 w-4" />
          Pending Requests
        </Button>
        <Button
          onClick={() => setActiveSubTab('deleted')}
          className={`rounded-full ${activeSubTab === 'deleted' ? 'bg-red-500' : 'bg-white/10'}`}
        >
          <UserX className="mr-2 h-4 w-4" />
          Deleted Users
        </Button>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading...</p>
          <div className="w-40 h-1 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full animate-pulse" style={{width: '70%'}} />
          </div>
        </div>
      ) : activeSubTab === 'pending' ? (
        /* Pending Deletion Requests */
        deletionRequests.length === 0 ? (
          <Card className="glass-panel p-8 text-center">
            <Check className="h-12 w-12 mx-auto mb-4 text-green-500" />
            <p className="text-muted-foreground">No pending deletion requests</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {deletionRequests.map((req) => (
              <Card key={req.request_id} className="glass-panel p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center overflow-hidden">
                      {req.user?.picture ? (
                        <img src={req.user.picture} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User className="h-6 w-6 text-muted-foreground" />
                      )}
                    </div>
                    <div>
                      <p className="font-bold">{req.user?.name || req.name}</p>
                      <p className="text-sm text-muted-foreground">{req.email}</p>
                      <p className="text-xs text-yellow-400 mt-1">
                        Requested: {new Date(req.requested_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(`/profile/${req.user_id}`, '_blank')}
                      className="rounded-full"
                    >
                      View Profile
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleApproveDelete(req.user_id)}
                      disabled={actionLoading === req.user_id}
                      className="bg-red-500 hover:bg-red-600 rounded-full"
                    >
                      {actionLoading === req.user_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )
      ) : (
        /* Deleted Users List */
        deletedUsers.length === 0 ? (
          <Card className="glass-panel p-8 text-center">
            <Check className="h-12 w-12 mx-auto mb-4 text-green-500" />
            <p className="text-muted-foreground">No deleted users</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {deletedUsers.map((user) => (
              <Card key={user.user_id} className="glass-panel p-4 border-red-500/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center overflow-hidden">
                      {user.picture ? (
                        <img src={user.picture} alt="" className="w-full h-full object-cover opacity-50" />
                      ) : (
                        <User className="h-6 w-6 text-red-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-bold text-red-300">{user.name}</p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                      <p className="text-xs text-red-400 mt-1">
                        Deleted: {user.deleted_at ? new Date(user.deleted_at).toLocaleDateString() : 'Unknown'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleRecoverAccount(user.user_id)}
                      disabled={actionLoading === user.user_id}
                      className="bg-green-500 hover:bg-green-600 rounded-full"
                    >
                      {actionLoading === user.user_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />}
                      Recover
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handlePermanentDelete(user.user_id)}
                      disabled={actionLoading === user.user_id}
                      className="rounded-full"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Permanently Delete
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )
      )}
    </div>
  );
}

export default AdminDashboard;